import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { sendMessage } from "../lib/api";

const SUBJECTS = [
  { value: "general", label: "general inquiry" },
  { value: "correction", label: "correction to a listing" },
  { value: "removal", label: "request removal of my tool" },
  { value: "press", label: "press / partnerships" },
  { value: "other", label: "other" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({
    subject: params.get("subject") || "general",
    name: "",
    email: "",
    tool_url: params.get("tool_url") || "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // preserve subject/tool_url from URL if someone lands via a "correct →" link
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message || !form.subject) {
      toast.error("Please fill email and message");
      return;
    }
    setBusy(true);
    try {
      await sendMessage({
        subject: form.subject,
        name: form.name.trim() || null,
        email: form.email.trim(),
        tool_url: form.tool_url.trim() || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Message sent. We reply within a few days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success-page">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-10">
          <div className="tv-card" data-testid="contact-success">
            <h1 className="text-xl font-bold">{">"} Got it.</h1>
            <p className="text-[13px] mt-2 text-[#444]">
              Your message is in the queue. A human will look at it, usually within a few days.
            </p>
            <div className="mt-4">
              <Link to="/" className="tv-btn" data-testid="back-home-btn">[←] back to index</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isCorrection = form.subject === "correction" || form.subject === "removal";

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        description="Contact TerminalValue.io — corrections, removal requests, press, partnerships. One human reads every message."
        path="/contact"
        noindex={params.toString() !== ""}
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-xl font-bold border-b border-black pb-2">{">"}_ Contact</h1>
        <p className="text-[13px] text-[#444] mt-2">
          Corrections, removal requests, press, or anything else. One human reads every message.
        </p>

        <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 gap-4" data-testid="contact-form">
          <Field label="What is this about? *">
            <select required className="tv-select" value={form.subject} onChange={update("subject")} data-testid="input-subject">
              {SUBJECTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>

          {isCorrection && (
            <Field label="Tool URL" hint="The listing you're writing about">
              <input type="text" className="tv-input" value={form.tool_url} onChange={update("tool_url")} placeholder="https://your-tool.com" data-testid="input-tool-url" />
            </Field>
          )}

          <Field label="Your email *">
            <input type="email" required maxLength={200} className="tv-input" value={form.email} onChange={update("email")} placeholder="you@domain.com" data-testid="input-email" />
          </Field>

          <Field label="Your name (optional)">
            <input type="text" maxLength={120} className="tv-input" value={form.name} onChange={update("name")} placeholder="Jane Doe" data-testid="input-name" />
          </Field>

          <Field label="Message *" hint={`${form.message.length}/4000`}>
            <textarea required maxLength={4000} rows={6} className="tv-textarea" value={form.message} onChange={update("message")} placeholder="What would you like us to know?" data-testid="input-message" />
          </Field>

          <div className="flex items-center gap-2 mt-2">
            <button type="submit" className="tv-btn tv-btn-primary" disabled={busy} data-testid="send-btn">
              {busy ? "[ sending... ]" : "[ send ]"}
            </button>
            <button type="button" className="tv-btn" onClick={() => nav("/")} data-testid="cancel-contact-btn">
              [esc] cancel
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="text-[12px] font-bold">{label}</span>
        {hint && <span className="text-[11px] text-[#888]">{hint}</span>}
      </div>
      <div className="mt-1">{children}</div>
    </label>
  );
}
